import { Injectable } from '@angular/core';

import { QuickDepositConfig } from '@frontend/sports/common/client-config-data-access';
import { DebugLogger, LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { generateGuid } from '@frontend/sports/common/core/utils/string';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { CashierService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { RedirectHelperService } from 'packages/sports/web/app/src/navigation-core/redirect-helper.service';
import { NumpadDisplayService } from 'packages/sports/web/app/src/numpad/numpad-display.service';
import { withLatestFrom } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipPlaceBetActions } from '../../../betplacement/actions';
import { PlaceButtonMode } from '../../../betplacement/state';
import { QuickDepositService } from '../../../quick-deposit/services/quick-deposit.service';
import { BetslipStakeActions } from '../../../stake/actions';
import BetslipErrorsActions from '../../../validation/actions';
import { selectHasInsufficientMoneyError } from '../../../validation/selectors';
import { StakeUpdateErrorModel } from '../../models';
import { TotalStake } from '../stake/stake-utils';
import { PlaceButtonTracking, PlaceButtonTrackingService } from './place-button-tracking.service';
import { IPlaceButtonActionHandlerService, PlaceButtonActionPredicate } from './viewmodel/interfaces/place-button-interfaces';

@Injectable()
export class PlaceButtonActionHandlerService implements IPlaceButtonActionHandlerService {
    private debugLogger: DebugLogger;

    constructor(
        protected store: Store<IBetslipRootState>,
        loggerFactory: LoggerFactory,
        private redirectHelper: RedirectHelperService,
        private cashierService: CashierService,
        private placeButtonTrackingService: PlaceButtonTrackingService,
        private quickDepositService: QuickDepositService,
        private quickDepositConfig: QuickDepositConfig,
        private numpadDisplayService: NumpadDisplayService,
        private userSettings: SyncUserSettingsService,
    ) {
        this.debugLogger = loggerFactory.getDebugLogger('BetslipActionService');
    }

    getConfirmHandler(): PlaceButtonActionPredicate {
        return this.confirmHandler;
    }

    makeDepositHandler(stakes: TotalStake[]): PlaceButtonActionPredicate {
        const totalStake = stakes.reduce((acc, curr) => acc + (curr.stake || 0), 0);

        return () => this.stakeDepositHandler(totalStake);
    }

    getDepositAndBetHandler(): PlaceButtonActionPredicate {
        return this.depositAndBetHandler;
    }

    getDepositHandler(): PlaceButtonActionPredicate {
        return this.depositHandler;
    }

    getAcceptChangesHandler(): PlaceButtonActionPredicate {
        return this.acceptChangesHandler;
    }

    getLoginHandler(): PlaceButtonActionPredicate {
        return this.loginHandler;
    }

    getUpdateMinStakeHandler(errorModels: StakeUpdateErrorModel[]): PlaceButtonActionPredicate {
        return () => this.updateMinStakeHandler(errorModels);
    }

    makePlaceHandler(isFreeBet: boolean): PlaceButtonActionPredicate {
        return () => this.placeHandler(isFreeBet);
    }

    makeAcceptAndPlaceBetHandler(isFreeBet: boolean): PlaceButtonActionPredicate {
        return () => this.acceptAndPlaceBetHandler(isFreeBet);
    }

    private stakeDepositHandler = (stake: number) => {
        this.doTracking(PlaceButtonTracking.MakeDeposit);
        if (this.quickDepositConfig.loadQuickDepositConfigOnLogin) {
            return this.loadQuickDeposit(stake);
        } else {
            this.quickDepositService.isQuickDepositEnabled().subscribe((_) => {
                return this.loadQuickDeposit(stake);
            });
        }
    };

    private loadQuickDeposit(stake: number) {
        if (this.quickDepositService.isEnabled) {
            this.quickDepositService.disableQuickDepositLoader();

            this.quickDepositService.open(stake);

            // eslint-disable-next-line no-bitwise
            this.store.dispatch(BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Deposit }));
        } else {
            this.cashierService.goToCashierDeposit({});
        }
    }

    private depositAndBetHandler = () => {
        this.doTracking(PlaceButtonTracking.DepositAndPlace);
        this.store.dispatch(BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Processing }));
        this.quickDepositService.keepOpen = true;

        this.quickDepositService
            .deposit()
            .pipe(withLatestFrom(this.store.select(selectHasInsufficientMoneyError)))
            .subscribe(([fundsAdded, hasInsufficientMoney]) => {
                if (fundsAdded.quickJourney && !hasInsufficientMoney) {
                    this.placeBet();

                    return;
                }
                if (fundsAdded.success) {
                    this.quickDepositService.keepOpen = false;
                }
                this.store.dispatch(BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Deposit }));
            });
    };

    private depositHandler = () => {
        this.doTracking(PlaceButtonTracking.Deposit);
        this.store.dispatch(BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Processing }));
        this.quickDepositService.deposit().subscribe((fundsAdded) => {
            if (!fundsAdded.success) {
                this.store.dispatch(BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Deposit }));
            }
        });
    };

    private updateMinStakeHandler(errorModels: StakeUpdateErrorModel[]): void {
        if (this.numpadDisplayService.isOpen()) {
            this.numpadDisplayService.close();
        }

        errorModels.forEach((updateError) => {
            const stake = updateError.error.minimumStake.toNumber();

            this.store.dispatch(BetslipStakeActions.updateMinStake({ minStake: stake, slipId: updateError.slipId, error: updateError.error }));
        });

        const eventDetails = errorModels.length > 1 ? PlaceButtonTracking.UpdateStakes : PlaceButtonTracking.UpdateStake;
        this.placeButtonTrackingService.updateStakeTrackData(PlaceButtonTracking.Betslip, eventDetails);
    }

    private acceptChangesHandler = () => {
        this.store.dispatch(BetslipErrorsActions.acceptBetslipChanges());
        //	this.placeButtonStateService.setState({ action: PlaceButtonStates.PlaceBet });
        this.doTracking(PlaceButtonTracking.AcceptChanges);
    };

    private loginHandler = () => {
        this.redirectHelper.goToLogin({ appendReferrer: true, useYahooLogin: true });
        this.doTracking(PlaceButtonTracking.LoginToBet);
    };

    private confirmHandler = () => {
        const requestId = generateGuid();
        this.store.dispatch(BetslipPlaceBetActions.requestPlaceBet());
        this.debugLogger.warning(`BetslipActionService.confirmHandler dispatch BetslipEvents.BetPlaceStarted ${requestId}`, true);
        // eslint-disable-next-line no-bitwise
        this.store.dispatch(BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Processing | PlaceButtonMode.Normal }));
        this.doTracking(PlaceButtonTracking.Confirm);
    };

    private placeHandler(isFreeBet: boolean): void {
        this.placeBet();
        if (isFreeBet) {
            this.doTracking(PlaceButtonTracking.PlaceFreeBet);

            return;
        }
        this.doTracking(PlaceButtonTracking.PlaceBet);
    }

    private acceptAndPlaceBetHandler(isFreeBet: boolean): void {
        this.store.dispatch(BetslipErrorsActions.acceptBetslipChanges());
        this.placeBet();
        if (isFreeBet) {
            this.doTracking(PlaceButtonTracking.AcceptAndPlaceFreeBet);
        } else {
            this.doTracking(PlaceButtonTracking.AcceptAndPlace);
        }
    }

    protected placeBet(): void {
        const skipConfirmation = this.userSettings.bettingSettings?.betslipSkipConfirmation;
        if (skipConfirmation) {
            const requestId = generateGuid();
            this.store.dispatch(BetslipPlaceBetActions.requestPlaceBet());
            this.debugLogger.warning(`BetslipActionService.placeBet dispatch BetslipEvents.BetPlaceStarted ${requestId}`, true);
        } else {
            this.quickDepositService.close(true);
            this.store.dispatch(BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Confirm }));
        }
    }

    protected doTracking(actionName: string): void {
        this.placeButtonTrackingService.track(actionName, PlaceButtonTracking.Betslip);
    }
}
